import { z } from "zod";
import {
  CalibrationPendencyKindSchema,
  CalibrationPendencyStatusSchema,
  ClassificationTargetSchema,
  DivergenceCauseSchema,
} from "./classification.js";

// Spec 077 (US2/US3) — a PENDÊNCIA DE CALIBRAÇÃO: o trabalho que a causa confirmada gerou.
//
// Prefixo próprio (`/calibration-pendencies`) e não aninhada sob o compromisso: a fila é
// transversal às linhas e quem a consome NÃO tem um `appliedScenarioId` em mãos. Pendurá-la sob o
// compromisso obrigaria o chamador a conhecer um recurso que ele está justamente procurando.

export const CalibrationPendencyQuerySchema = z.object({
  /** Default `OPEN`: a fila é sobre trabalho a fazer; o histórico se pede explicitamente. */
  status: CalibrationPendencyStatusSchema.default("OPEN"),
  lineId: z.string().uuid().optional(),
  kind: CalibrationPendencyKindSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
});

export const CalibrationPendencyItemSchema = z.object({
  id: z.string().uuid(),
  /** O caminho de volta ao compromisso de origem (FR-044) — a pendência sem contexto não é acionável. */
  appliedScenarioId: z.string().uuid(),
  lineId: z.string().uuid(),
  lineName: z.string(),
  kind: CalibrationPendencyKindSchema,
  status: CalibrationPendencyStatusSchema,
  /** A causa que a originou. Viaja porque o tipo não a reconstrói: três causas, três tipos. */
  cause: DivergenceCauseSchema,
  target: ClassificationTargetSchema.nullable(),
  createdAt: z.string(),
  /** DERIVADO (FR-040) — `now − createdAt`. Nunca coluna: envelheceria parada no banco. */
  ageDays: z.number().int(),
  resolvedAt: z.string().nullable(),
  resolvedByName: z.string().nullable(),
  resolutionNote: z.string().nullable(),
});
export type CalibrationPendencyItem = z.infer<typeof CalibrationPendencyItemSchema>;

export const CalibrationPendencyListEnvelopeSchema = z.object({
  data: z.object({
    /** Contagem REAL, independente da página (FR-037). */
    total: z.number().int(),
    items: z.array(CalibrationPendencyItemSchema),
  }),
  error: z.null(),
});

export const CalibrationPendencyEnvelopeSchema = z.object({
  data: CalibrationPendencyItemSchema,
  error: z.null(),
});

/**
 * O corpo de `POST /calibration-pendencies/:id/resolution`.
 *
 * ⚠️ `SUPERSEDED` NÃO é aceitável aqui: é efeito de reclassificação, dentro da transação que a
 * grava — nunca ação de usuário. Aceitá-lo daria a alguém a forma de encerrar uma pendência sem
 * dizer o que se fez, com aparência de conformidade.
 *
 * A nota é obrigatória nos DOIS desfechos (V8). Em `RESOLVED` ela é o que mudou no modelo — sem ela
 * a iteração 4 não consegue distinguir calibração de arquivamento. Em `DISCARDED` ela é a prestação
 * de contas de tirar trabalho da fila de outra pessoa.
 */
export const ResolvePendencyBodySchema = z.object({
  outcome: z.enum(["RESOLVED", "DISCARDED"]),
  note: z.string().min(1).max(1000),
});
export type ResolvePendencyBody = z.infer<typeof ResolvePendencyBodySchema>;
